import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Scale, Globe2, Zap, Users, ClipboardCheck, FlaskConical, ShieldCheck, Ship, Wheat, Shirt, HardHat, HeartPulse, PawPrint, Cpu, Factory, Fuel, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { heroStats, commitments, checkpoints, whyAqc, standards } from '../data/mock';
import { SectionHeading, LearnMore, Watermark } from '../components/Section';
import { heroImage, aboutImage, ctaImage } from '../data/mock';
import { useServices, useIndustries, usePage } from '../lib/cms';
import SEO from '../lib/SEO';

const iconMap = { Scale, Globe2, Zap, Users, ClipboardCheck, FlaskConical, ShieldCheck, Ship, Wheat, Shirt, HardHat, HeartPulse, PawPrint, Cpu, Factory, Fuel };

export default function Home() {
  const services = useServices();
  const industries = useIndustries();
  const { content: page } = usePage('home');
  const hero = page.hero || {};
  const cta = page.cta || {};
  return (
    <div className="font-sans-body">
      <SEO />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <Watermark color="#0F2A47" opacity={0.04} size={780} />
        <div className="container-aqc relative grid lg:grid-cols-2 gap-10 lg:gap-16 items-center py-16 lg:py-24">
          <div className="aqc-rise">
            <span className="eyebrow">{hero.eyebrow || 'Independent Inspection · Testing · Audit'}</span>
            <h1 className="mt-5 text-[2.6rem] sm:text-[3.4rem] leading-[1.05] font-semibold text-aqc-navy">
              {hero.title || 'Quality You Can Trust.'}<br />
              <span className="text-aqc-red italic">{hero.italic || 'Excellence We Deliver.'}</span>
            </h1>
            <p className="mt-6 text-[1.05rem] leading-relaxed text-aqc-muted max-w-xl">
              {hero.subtitle || 'Apex Quality Control provides independent inspection, testing, and audit services that help businesses ensure compliance, reduce risk, and deliver the highest quality to their customers — from the factory floor to final shipment.'}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact">
                <Button className="bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white h-12 px-6 rounded-md text-[0.95rem]">
                  {hero.cta_primary || 'Get in Touch'}
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="h-12 px-6 rounded-md text-[0.95rem] border-aqc-navy text-aqc-navy hover:bg-aqc-navy hover:text-white">
                  {hero.cta_secondary || 'Explore Services'}
                </Button>
              </Link>
            </div>
            <ul className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 max-w-xl">
              {heroStats.map((s) => (
                <li key={s.label} className="flex items-center gap-2 text-[0.83rem] text-aqc-ink/85">
                  <span className="w-2 h-2 rotate-45 bg-aqc-red" />
                  <span className="font-medium">{s.label}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative aqc-rise-2">
            <div className="relative rounded-[20px] overflow-hidden shadow-[0_30px_60px_-30px_rgba(15,42,71,0.35)] border border-aqc-line">
              <img src={heroImage} alt="AQC quality inspector at work on the factory floor" className="w-full h-[420px] lg:h-[540px] object-cover" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur px-4 py-3 rounded-lg border border-aqc-line flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-aqc-red/10 flex items-center justify-center text-aqc-red">
                  <CheckCircle2 size={18} />
                </div>
                <div>
                  <div className="text-[0.72rem] uppercase tracking-[0.14em] text-aqc-muted font-semibold">Report</div>
                  <div className="text-sm font-semibold text-aqc-navy">Delivered within 24 hours</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitments strip */}
      <section className="bg-navy-grid text-white">
        <div className="container-aqc py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {commitments.map((c) => {
            const Icon = iconMap[c.icon] || CheckCircle2;
            return (
              <div key={c.title} className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-full bg-white/8 border border-white/15 flex items-center justify-center shrink-0">
                  <Icon size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-white">{c.title}</h3>
                  <p className="text-white/70 text-sm mt-1 leading-relaxed">{c.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Core services */}
      <section className="relative py-20 lg:py-24">
        <Watermark opacity={0.03} />
        <div className="container-aqc relative">
          <SectionHeading
            eyebrow="Our Core Services"
            title={<>End-to-end quality assurance.</>}
            subtitle="Four core capabilities. Twelve service types. One coordinated programme tailored to your product and your factories."
          />
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {services.map((s, i) => {
              const Icon = iconMap[s.icon];
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="card-lift group bg-white rounded-2xl border border-aqc-line overflow-hidden">
                  <div className="relative h-[240px] overflow-hidden">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-aqc-navy/40 to-transparent" />
                    <div className="absolute bottom-4 left-4 h-11 w-11 rounded-full bg-aqc-navy text-white flex items-center justify-center shadow-lg">
                      <Icon size={18} />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-[1.4rem] text-aqc-navy group-hover:text-aqc-red transition-colors">{s.title}</h3>
                    <p className="mt-2 text-aqc-muted text-[0.95rem] leading-relaxed">{s.tagline}</p>
                    <div className="mt-5"><LearnMore to={`/services/${s.slug}`} /></div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Four checkpoints */}
      <section className="relative py-20 lg:py-24 bg-aqc-cream">
        <div className="container-aqc">
          <SectionHeading
            eyebrow="How We Work"
            title={<>Four Checkpoints. One Continuous Chain of Quality.</>}
            subtitle="Every AQC programme is structured around four stages that map to your production cycle — so risk is caught at the earliest possible point."
          />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {checkpoints.map((c) => (
              <div key={c.n} className="relative bg-white border border-aqc-line rounded-2xl p-8 text-center card-lift">
                <div className="mx-auto h-20 w-20 rounded-full bg-aqc-navy text-white font-serif text-2xl flex items-center justify-center relative before:absolute before:inset-[-8px] before:rounded-full before:border-2 before:border-dashed before:border-aqc-red/40">
                  {c.n}
                </div>
                <div className="mt-5 inline-block text-[0.72rem] font-bold tracking-[0.18em] uppercase text-aqc-red bg-aqc-red/10 rounded-full px-3 py-1">
                  {c.code}
                </div>
                <h3 className="mt-3 font-serif text-xl text-aqc-navy">{c.title}</h3>
                <p className="mt-2 text-sm text-aqc-muted leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="relative py-20 lg:py-24 bg-navy-grid text-white overflow-hidden">
        <Watermark color="#FFFFFF" opacity={0.05} size={640} />
        <div className="container-aqc relative">
          <div className="text-center">
            <div className="flex justify-center"><span className="eyebrow text-white/70 before:bg-white/50 after:bg-white/50">Industries We Serve</span></div>
            <h2 className="mt-4 text-[2.1rem] sm:text-[2.6rem] leading-[1.1] font-semibold">Sector expertise. <span className="italic">Real-world context.</span></h2>
            <div className="section-title-divider" />
            <p className="text-white/75 max-w-2xl mx-auto">Eight sectors — with distinct standards and distinct failure modes. We bring the right checklist to your product.</p>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {industries.map((ind) => {
              const Icon = iconMap[ind.icon] || Factory;
              return (
                <Link key={ind.id} to={`/industries#${ind.id}`} className="group bg-white/5 hover:bg-white/10 border border-white/15 rounded-2xl p-6 transition-colors">
                  <div className="h-11 w-11 rounded-full bg-aqc-red flex items-center justify-center mb-4">
                    <Icon size={18} className="text-white" />
                  </div>
                  <h3 className="font-serif text-lg leading-snug">{ind.title}</h3>
                  <p className="mt-2 text-[0.88rem] text-white/70">{ind.short}</p>
                  <div className="mt-4 text-[0.78rem] uppercase tracking-[0.14em] text-white/80 font-semibold flex items-center gap-1 group-hover:text-white">
                    Read more <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why AQC */}
      <section className="relative py-20 lg:py-24">
        <div className="container-aqc grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative">
            <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-aqc-red/10" />
            <img src={aboutImage} alt="AQC coordination team" className="relative rounded-2xl w-full h-[420px] object-cover shadow-[0_30px_60px_-30px_rgba(15,42,71,0.4)]" />
            <div className="absolute -bottom-6 -right-6 bg-aqc-navy text-white rounded-2xl p-5 shadow-xl w-[220px]">
              <div className="font-serif text-4xl">10+</div>
              <div className="text-white/80 text-sm mt-1">Countries Coverage</div>
              <div className="h-px bg-white/20 my-3" />
              <div className="font-serif text-4xl">8</div>
              <div className="text-white/80 text-sm mt-1">Industries Served</div>
            </div>
          </div>
          <div>
            <SectionHeading center={false} eyebrow="Why AQC" title={<>Why global brands and startups choose AQC.</>} />
            <ul className="mt-8 space-y-4">
              {whyAqc.map((w) => (
                <li key={w.title} className="flex items-start gap-3">
                  <div className="mt-1 h-5 w-5 rounded-full bg-aqc-red/12 flex items-center justify-center shrink-0">
                    <CheckCircle2 size={14} className="text-aqc-red" />
                  </div>
                  <p className="text-[0.98rem] leading-relaxed">
                    <span className="font-semibold text-aqc-navy">{w.title}</span>{' '}
                    <span className="text-aqc-muted">{w.body}</span>
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Standards */}
      <section className="relative py-20 lg:py-24 bg-aqc-cream">
        <div className="container-aqc">
          <SectionHeading
            eyebrow="Aligned to International Standards"
            title="A shared language of quality."
            subtitle="We apply the same standards buyers, regulators and accredited labs use — so our findings are comparable to yours."
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {standards.slice(0, 8).map((s) => (
              <div key={s.code} className="bg-white border border-aqc-line rounded-xl p-5 card-lift">
                <div className="text-[0.7rem] uppercase tracking-[0.16em] text-aqc-red font-semibold">Standard</div>
                <div className="font-serif text-lg text-aqc-navy mt-1.5">{s.code}</div>
                <p className="text-[0.86rem] text-aqc-muted mt-2 leading-relaxed line-clamp-3">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/standards" className="link-underline text-[0.85rem] uppercase tracking-[0.14em]">See the full standards table <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <img src={ctaImage} alt="Port at dusk" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-aqc-navy/85" />
        <div className="relative container-aqc py-24 text-center text-white">
          <div className="flex justify-center"><span className="eyebrow text-white/80 before:bg-white/40 after:bg-white/40">{cta.eyebrow || 'Ready when you are'}</span></div>
          <h2 className="mt-4 font-serif text-[2rem] sm:text-[2.6rem] leading-tight max-w-3xl mx-auto">
            {cta.title || 'Partner with AQC for quality without compromise.'}
          </h2>
          <div className="mt-8">
            <Link to="/contact">
              <Button className="bg-aqc-red hover:brightness-110 text-white h-12 px-8 rounded-md text-[0.95rem]">{cta.button || 'Contact Us'}</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
